import { trackEvent } from './analytics';

type ScrollMarks = { [key: number]: boolean };

export const initScrollTracking = () => {
  const scrollMarks: ScrollMarks = { 25: false, 50: false, 75: false, 90: false };
  
  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    Object.keys(scrollMarks).forEach((mark) => {
      const markNum = parseInt(mark);
      if (scrollPercent >= markNum && !scrollMarks[markNum]) {
        scrollMarks[markNum] = true;
        trackEvent('scroll', {
          percent: markNum,
          page: window.location.pathname
        });
      }
    });
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
};

export const initTimeTracking = () => {
  const startTime = Date.now();
  
  const trackTimeOnPage = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    trackEvent('time_on_page', {
      page: window.location.pathname,
      seconds: timeSpent
    });
  };
  
  window.addEventListener('beforeunload', trackTimeOnPage);
  
  const interval = setInterval(() => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    if (timeSpent % 30 === 0 && timeSpent > 0) {
      trackEvent('time_milestone', {
        page: window.location.pathname,
        seconds: timeSpent
      });
    }
  }, 1000);
  
  return () => {
    clearInterval(interval);
    window.removeEventListener('beforeunload', trackTimeOnPage);
  };
};

export const trackExternalLink = (url: string, linkText: string) => {
  trackEvent('click_external_link', {
    link_url: url,
    link_text: linkText,
    outbound: true
  });
};

export const trackEmailClick = (email: string) => {
  trackEvent('click_email', {
    email: email,
    page: window.location.pathname
  });
};

export const trackVideoPlay = (videoTitle: string, videoUrl?: string) => {
  trackEvent('video_start', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

export const trackVideoComplete = (videoTitle: string) => {
  trackEvent('video_complete', {
    video_title: videoTitle
  });
};

export const trackFormStart = (formName: string) => {
  trackEvent('form_start', {
    form_name: formName,
    page: window.location.pathname
  });
};

export const trackFormSubmit = (formName: string, formData?: Record<string, unknown>) => {
  trackEvent('form_submit', {
    form_name: formName,
    page: window.location.pathname,
    ...formData
  });
};

export const trackFormError = (formName: string, errorField: string, errorMessage: string) => {
  trackEvent('form_error', {
    form_name: formName,
    error_field: errorField,
    error_message: errorMessage
  });
};

export const trackLeadGeneration = (source: string, value?: number) => {
  trackEvent('generate_lead', {
    source: source,
    currency: 'EUR',
    value: value || 0
  });
};

export const trackNewsletterSignup = (source: string) => {
  trackEvent('newsletter_signup', {
    source: source,
    page: window.location.pathname
  });
};

export const trackApplicationStart = (applicationType: string) => {
  trackEvent('application_start', {
    application_type: applicationType
  });
};

export const trackApplicationSubmit = (applicationType: string) => {
  trackEvent('application_submit', {
    application_type: applicationType
  });
};

export const trackFAQClick = (question: string, section: string) => {
  trackEvent('faq_click', {
    question: question.substring(0, 100),
    section: section,
    page: window.location.pathname
  });
};

export const trackTemplatePreview = (action: string, details?: string) => {
  trackEvent('template_preview', {
    action: action,
    details: details || '',
    page: window.location.pathname
  });
};

export const trackTermsClick = (source: string) => {
  trackEvent('terms_click', {
    source: source,
    page: window.location.pathname
  });
};

export const trackTermsAcceptance = (productType: string) => {
  trackEvent('terms_accept', {
    product_type: productType,
    page: window.location.pathname
  });
};

export const trackTestimonialView = (testimonialAuthor: string) => {
  trackEvent('testimonial_view', {
    author: testimonialAuthor,
    page: window.location.pathname
  });
};

export const trackReviewInteraction = (action: string) => {
  trackEvent('review_interaction', {
    action: action
  });
};

export const trackPortfolioClick = (creatorName: string, portfolioItem?: string) => {
  trackEvent('portfolio_click', {
    creator: creatorName,
    item: portfolioItem || 'profile',
    page: window.location.pathname
  });
};

export const trackCTAClick = (ctaName: string, ctaLocation: string) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    page: window.location.pathname
  });
};

export const trackDownloadRetry = (reason: string) => {
  trackEvent('download_retry', {
    reason: reason
  });
};

export const trackCheckoutStep = (step: number, stepName: string, productInfo?: Record<string, unknown>) => {
  trackEvent('checkout_progress', {
    checkout_step: step,
    checkout_step_name: stepName,
    ...productInfo
  });
};

export const trackCheckoutAbandonment = (step: string, reason?: string) => {
  trackEvent('checkout_abandon', {
    abandoned_at: step,
    reason: reason || 'user_left',
    page: window.location.pathname
  });
};

export const trackAddToCart = (productId: string, productName: string, price: number) => {
  trackEvent('add_to_cart', {
    currency: 'EUR',
    value: price,
    items: [{
      item_id: productId,
      item_name: productName,
      price: price
    }]
  });
};

export const trackRemoveFromCart = (productId: string, productName: string) => {
  trackEvent('remove_from_cart', {
    items: [{
      item_id: productId,
      item_name: productName
    }]
  });
};

export const initBounceIntentTracking = () => {
  let hasTracked = false;
  
  const trackBounceIntent = (e: MouseEvent) => {
    if (!hasTracked && e.clientY <= 0) {
      hasTracked = true;
      trackEvent('bounce_intent', {
        page: window.location.pathname,
        time_on_page: Math.round(performance.now() / 1000)
      });
    }
  };
  
  document.addEventListener('mouseout', trackBounceIntent);
  return () => document.removeEventListener('mouseout', trackBounceIntent);
};

export const trackSessionDepth = (pagesViewed: number) => {
  trackEvent('session_depth', {
    pages_viewed: pagesViewed
  });
};

export const trackPriceInteraction = (action: string, productName: string) => {
  trackEvent('price_interaction', {
    action: action,
    product: productName
  });
};

export const initInactivityTracking = (timeoutMinutes: number = 5) => {
  let inactivityTimer: ReturnType<typeof setTimeout>;
  
  const resetTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      trackEvent('user_inactive', {
        page: window.location.pathname,
        inactive_minutes: timeoutMinutes
      });
    }, timeoutMinutes * 60 * 1000);
  };
  
  ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
    document.addEventListener(event, resetTimer, true);
  });
  
  resetTimer();
  
  return () => {
    clearTimeout(inactivityTimer);
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.removeEventListener(event, resetTimer, true);
    });
  };
};

export const trackLoginAttempt = (method: string) => {
  trackEvent('login_attempt', {
    method: method
  });
};

export const trackLoginSuccess = (userId: string, userRole: string) => {
  trackEvent('login', {
    method: 'authenticated',
    user_role: userRole
  });
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      user_role: userRole
    });
  }
};

export const trackLoginFailure = (reason: string) => {
  trackEvent('login_failed', {
    reason: reason
  });
};

export const trackSignup = (method: string, userType: string) => {
  trackEvent('sign_up', {
    method: method,
    user_type: userType
  });
};

export const trackLogout = () => {
  trackEvent('logout');
};

export const trackProfileUpdate = (field: string) => {
  trackEvent('profile_update', {
    updated_field: field
  });
};

export const trackProfileCompletion = (completionPercentage: number) => {
  trackEvent('profile_completion', {
    completion_percentage: completionPercentage
  });
};

export const trackPortfolioAction = (action: string, itemType?: string) => {
  trackEvent('portfolio_action', {
    action: action,
    item_type: itemType
  });
};

export const trackApplicationAction = (action: string, status?: string) => {
  trackEvent('application_action', {
    action: action,
    status: status
  });
};

export const trackCollaborationAction = (action: string, brandName?: string) => {
  trackEvent('collaboration_action', {
    action: action,
    brand: brandName
  });
};

export const trackCampaignAction = (action: string, campaignId?: string) => {
  trackEvent('campaign_action', {
    action: action,
    campaign_id: campaignId
  });
};

export const trackCreatorSearch = (filters: Record<string, unknown>) => {
  trackEvent('creator_search', {
    filters: JSON.stringify(filters)
  });
};

export const trackCreatorContact = (creatorId: string, method: string) => {
  trackEvent('creator_contact', {
    creator_id: creatorId,
    contact_method: method
  });
};

export const trackDocumentAction = (action: string, documentType: string) => {
  trackEvent('document_action', {
    action: action,
    document_type: documentType
  });
};

export const trackFeatureUsage = (featureName: string, timeSpent?: number) => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    time_spent_seconds: timeSpent
  });
};

export const trackOnboardingStep = (step: number, stepName: string, completed: boolean) => {
  trackEvent('onboarding_progress', {
    step: step,
    step_name: stepName,
    completed: completed
  });
};
